/**
 * The borough clock and the weather, shared by the client (offline: its own clock, src/render/timeOfDay.ts)
 * and the server (online: the shard's clock and weather, server/clock/clockModule.ts, protocol v23).
 *
 * Online the shard's sky is a pure function of the server's wall clock and the shard id: no state, the
 * same after a restart, and every player in a shard sees the same hour and the same rain. The server
 * sends it as an anchor (`clock` push: the hour at a server time, and the weather); clients run the
 * hour on from there on their synced server clock.
 */

export type SkyWeather = 'clear' | 'drizzle' | 'rain';

/**
 * Game hours per second. An hour is 90 s of play, so a full day takes 36 minutes: long enough that day,
 * evening and night each feel like a stretch of play, with the light still visibly moving through a session.
 */
export const CLOCK_HOURS_PER_SECOND = 1 / 90;
/** Offline the clock starts here on every boot (the signature look: drizzly dusk, before the long fade into night). */
export const CLOCK_START_HOUR = 18.5;

/** A spell of one weather online (real ms): the shard's rain sets in and lifts on this beat. */
export const WEATHER_SPELL_MS = 9 * 60_000;
/** How often the server repeats the anchor to everyone in a shard (ms); a change of weather goes out at once. */
export const CLOCK_SYNC_MS = 60_000;

/** Odds of each weather for a spell: mostly dry or drizzly, now and then proper rain. */
const WEATHER_ODDS: readonly [SkyWeather, number][] = [
  ['clear', 0.45],
  ['drizzle', 0.4],
  ['rain', 0.15],
];

/** FNV-1a over a string, then mixed with `n`: a stable 0..1 per (shard, n). */
function hash01(key: string, n: number): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  h ^= n | 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 0x1_0000_0000;
}

const wrap24 = (h: number): number => ((h % 24) + 24) % 24;

/** The shard's hour (0..24) at server time `now` (ms). Each shard runs its own day, offset by its id. */
export function shardHour(shardId: string, now: number): number {
  const offset = hash01(shardId, -1) * 24;
  // Reduce the epoch part first so the float keeps its precision.
  const days = now / 1000 / (24 / CLOCK_HOURS_PER_SECOND);
  return wrap24((days - Math.floor(days)) * 24 + offset);
}

/** The shard's weather at server time `now` (ms): one spell of WEATHER_SPELL_MS at a time. */
export function shardWeather(shardId: string, now: number): SkyWeather {
  const r = hash01(shardId, Math.floor(now / WEATHER_SPELL_MS));
  let acc = 0;
  for (const [w, p] of WEATHER_ODDS) {
    acc += p;
    if (r < acc) return w;
  }
  return 'clear';
}

/** What the `clock` push carries: the hour at server time `at`, and the weather now. */
export interface ClockMsg {
  /** Hour (0..24, 3 decimals) at `at`. */
  h: number;
  /** Server time (ms) the hour was read at. */
  at: number;
  w: SkyWeather;
}

export function clockMsg(shardId: string, now: number): ClockMsg {
  return { h: Math.round(shardHour(shardId, now) * 1000) / 1000, at: now, w: shardWeather(shardId, now) };
}

/** The hour a `clock` anchor gives at server time `serverNow` (ms). */
export function hourFromAnchor(m: Pick<ClockMsg, 'h' | 'at'>, serverNow: number): number {
  return wrap24(m.h + ((serverNow - m.at) / 1000) * CLOCK_HOURS_PER_SECOND);
}
